import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { generateSeoContent, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
} from "recharts";
import {
  MagicWand, Globe, Hash, Copy, Check, ArrowClockwise, Export, Star, Lightning,
  Robot, Target, Sparkle, Code, FileText, Tag, ChatCircle, Eye, Crown, Lock,
  ArrowRight, Plus, X, Trash, Clock, MagnifyingGlass, Heart, Info, Warning,
  Fire, Lightbulb, BookOpen, Gauge, ArrowUpRight, Pencil, Brain, Funnel,
  InstagramLogo, YoutubeLogo, LinkedinLogo, FacebookLogo, RedditLogo,
  TiktokLogo, PinterestLogo, XLogo, TrendUp, ChartBar, Link as LinkIcon,
  CaretDown,
} from "@phosphor-icons/react";

// ─── Platform Config ──────────────────────────────────────────────────────────

const PLATFORMS = [
  {
    id: "google", label: "Google SEO", tagline: "Search rankings",
    color: "#4285F4", bgColor: "rgba(66,133,244,0.08)", borderColor: "rgba(66,133,244,0.3)",
    iconText: "G", iconBg: "#4285F4",
  },
  {
    id: "reddit", label: "Reddit", tagline: "Community threads",
    color: "#FF4500", bgColor: "rgba(255,69,0,0.08)", borderColor: "rgba(255,69,0,0.3)",
    Icon: RedditLogo,
  },
  {
    id: "x", label: "X (Twitter)", tagline: "Viral tweets",
    color: "#1d9bf0", bgColor: "rgba(29,155,240,0.08)", borderColor: "rgba(29,155,240,0.3)",
    Icon: XLogo,
  },
  {
    id: "instagram", label: "Instagram", tagline: "Visual captions",
    color: "#E1306C", bgColor: "rgba(225,48,108,0.08)", borderColor: "rgba(225,48,108,0.3)",
    Icon: InstagramLogo,
  },
  {
    id: "facebook", label: "Facebook", tagline: "Organic reach",
    color: "#1877F2", bgColor: "rgba(24,119,242,0.08)", borderColor: "rgba(24,119,242,0.3)",
    Icon: FacebookLogo,
  },
  {
    id: "linkedin", label: "LinkedIn", tagline: "Professional reach",
    color: "#0A66C2", bgColor: "rgba(10,102,194,0.08)", borderColor: "rgba(10,102,194,0.3)",
    Icon: LinkedinLogo,
  },
  {
    id: "youtube", label: "YouTube", tagline: "Video SEO",
    color: "#FF0000", bgColor: "rgba(255,0,0,0.08)", borderColor: "rgba(255,0,0,0.3)",
    Icon: YoutubeLogo,
  },
  {
    id: "tiktok", label: "TikTok", tagline: "Viral hooks",
    color: "#FE2C55", bgColor: "rgba(254,44,85,0.08)", borderColor: "rgba(254,44,85,0.3)",
    Icon: TiktokLogo,
  },
  {
    id: "pinterest", label: "Pinterest", tagline: "Discovery pins",
    color: "#E60023", bgColor: "rgba(230,0,35,0.08)", borderColor: "rgba(230,0,35,0.3)",
    Icon: PinterestLogo,
  },
  {
    id: "medium", label: "Medium", tagline: "Long-form articles",
    color: "#00AB6C", bgColor: "rgba(0,171,108,0.08)", borderColor: "rgba(0,171,108,0.3)",
    iconText: "M", iconBg: "#00AB6C",
  },
];

const TONES = ["Professional", "Viral", "Casual", "Technical", "Marketing", "Engaging"];
const AUDIENCES = ["Developers", "Startups", "Marketers", "Businesses", "General Audience", "Students", "Entrepreneurs"];
const LENGTHS = ["Short", "Medium", "Long", "Long-form"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Japanese", "Chinese"];

const LOADING_STEPS = [
  "Analyzing SEO patterns…",
  "Extracting semantic keywords…",
  "Generating viral hooks…",
  "Optimizing metadata…",
  "Building platform captions…",
  "Calculating SEO score…",
  "Constructing schema markup…",
  "Finalizing output…",
];

const FREE_CONTENT_LIMIT = 500;
const PREMIUM_CONTENT_LIMIT = 5000;
const FREE_KEYWORD_LIMIT = 5;
const PREMIUM_KEYWORD_LIMIT = 25;
const FREE_PLATFORM_LIMIT = 1;
const FREE_DAILY_LIMIT = 5;

// ─── Sub-components ───────────────────────────────────────────────────────────

function PlatformCard({ platform, selected, onToggle, disabled }) {
  const { Icon, iconText, iconBg, label, tagline, color, bgColor, borderColor } = platform;
  return (
    <button
      onClick={() => onToggle(platform.id)}
      disabled={disabled && !selected}
      data-testid={`platform-card-${platform.id}`}
      className={cn(
        "relative group flex flex-col items-start gap-1.5 p-3.5 border transition-all duration-200 text-left w-full",
        "rounded-lg",
        selected
          ? "border-opacity-100 shadow-sm"
          : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300",
        disabled && !selected && "opacity-40 cursor-not-allowed",
      )}
      style={selected ? { backgroundColor: bgColor, borderColor, boxShadow: `0 0 0 1px ${borderColor}` } : {}}
      aria-pressed={selected}
    >
      {selected && (
        <span className="absolute top-2 right-2">
          <Check size={11} weight="bold" style={{ color }} />
        </span>
      )}
      <div
        className="flex items-center justify-center w-7 h-7 rounded-md"
        style={selected ? { backgroundColor: `${color}20`, color } : { backgroundColor: "#F1F5F9", color: "#94A3B8" }}
      >
        {Icon ? (
          <Icon size={16} weight="fill" />
        ) : (
          <span className="font-bold text-sm" style={selected ? { color } : { color: "#94A3B8" }}>
            {iconText}
          </span>
        )}
      </div>
      <div>
        <div className={cn("text-xs font-semibold leading-none", selected ? "" : "text-slate-600")} style={selected ? { color } : {}}>
          {label}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5 leading-none">{tagline}</div>
      </div>
    </button>
  );
}

function CharCounter({ current, max, label, isPremium }) {
  const pct = Math.min((current / max) * 100, 100);
  const isNear = pct >= 80;
  const isOver = pct >= 100;
  const barColor = isOver ? "#ef4444" : isNear ? "#f59e0b" : isPremium ? "#a78bfa" : "#2563EB";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-400">{label}</span>
        <span
          className={cn(
            "text-[10px] tabular-nums",
            isOver ? "text-red-500" : isNear ? "text-amber-500" : "text-slate-400",
          )}
        >
          {current.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div className="h-0.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      {isNear && !isOver && !isPremium && (
        <p className="text-[10px] text-amber-600">Approaching free tier limit</p>
      )}
    </div>
  );
}

function KeywordTag({ keyword, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-600 text-xs rounded font-medium">
      {keyword}
      <button onClick={() => onRemove(keyword)} className="hover:text-blue-800 transition-colors ml-0.5" aria-label={`Remove ${keyword}`}>
        <X size={10} weight="bold" />
      </button>
    </span>
  );
}

function OutputCard({ title, content, icon: Icon, mono, onCopy, copied, premium, locked }) {
  return (
    <div
      className={cn(
        "border border-slate-200 bg-white rounded-xl p-4 group relative",
        locked && "opacity-60",
      )}
      data-testid={`output-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={13} className="text-slate-400" />}
          <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{title}</span>
          {premium && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-violet-50 border border-violet-200 text-violet-600 text-[11px] uppercase tracking-wider rounded">
              <Crown size={8} />Pro
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {locked ? (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <Lock size={11} />
              <span>Premium</span>
            </span>
          ) : (
            <button
              onClick={onCopy}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-900 border border-transparent hover:border-slate-200 rounded transition-all"
              aria-label={`Copy ${title}`}
            >
              {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          )}
        </div>
      </div>
      {locked ? (
        <div className="h-10 flex items-center gap-2 text-slate-400 text-xs">
          <Lock size={13} />
          <span>Unlock with Premium to view this output</span>
        </div>
      ) : (
        <div className={cn("text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words", mono && "font-mono text-xs text-slate-500")}>
          {Array.isArray(content) ? (
            <ul className="space-y-1">
              {content.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">·</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : content}
        </div>
      )}
    </div>
  );
}

function UpgradeBanner({ message, compact }) {
  return (
    <div className={cn(
      "flex items-center gap-3 border border-violet-200 bg-violet-50 rounded-lg",
      compact ? "p-3" : "p-4",
    )}>
      <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 bg-violet-100 rounded-lg">
        <Crown size={14} className="text-violet-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-violet-700 leading-snug">{message}</p>
      </div>
      <Link
        to="/pricing"
        className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg transition-colors"
      >
        Upgrade <ArrowRight size={11} />
      </Link>
    </div>
  );
}

function ScoreRing({ score, label, color }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
          <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="4" />
          <circle
            cx="28" cy="28" r="22" fill="none"
            stroke={color || "#2563EB"} strokeWidth="4"
            strokeDasharray={`${(score / 100) * 138} 138`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 1s ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-slate-900">{score}</span>
        </div>
      </div>
      <span className="text-[10px] text-slate-400 text-center leading-tight">{label}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SEOStudioPage() {
  const { user, setAuthModalOpen } = useAuth();
  const isPremium = useMemo(() => user?.is_premium === true || user?.role === "admin", [user]);

  const contentLimit = isPremium ? PREMIUM_CONTENT_LIMIT : FREE_CONTENT_LIMIT;
  const keywordLimit = isPremium ? PREMIUM_KEYWORD_LIMIT : FREE_KEYWORD_LIMIT;
  const platformLimit = isPremium ? PLATFORMS.length : FREE_PLATFORM_LIMIT;

  // ── form state ──
  const [sourceUrl, setSourceUrl] = useState("");
  const [content, setContent] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [tone, setTone] = useState("Professional");
  const [audience, setAudience] = useState("General Audience");
  const [length, setLength] = useState("Medium");
  const [language, setLanguage] = useState("English");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["google"]);

  // ── generation state ──
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [output, setOutput] = useState(null);
  const [generateError, setGenerateError] = useState(null);

  // ── ui state ──
  const [copiedKey, setCopiedKey] = useState(null);
  const [activeOutputTab, setActiveOutputTab] = useState("seo");
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tm_seo_history") || "[]");
    } catch {
      return [];
    }
  });
  const [historySearch, setHistorySearch] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const outputRef = useRef(null);
  const textareaRef = useRef(null);

  // ── auto-resize textarea ──
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [content]);

  const charCount = content.length;
  const tokenEstimate = Math.ceil(charCount / 4);
  const isNearLimit = charCount >= contentLimit * 0.8;
  const isOverLimit = charCount > contentLimit;

  // ── platform toggle ──
  const handlePlatformToggle = useCallback((id) => {
    setSelectedPlatforms((prev) => {
      if (prev.includes(id)) {
        return prev.length === 1 ? prev : prev.filter((p) => p !== id);
      }
      if (!isPremium && prev.length >= FREE_PLATFORM_LIMIT) return prev;
      return [...prev, id];
    });
  }, [isPremium]);

  // ── keyword management ──
  const handleKeywordAdd = useCallback((e) => {
    if ((e.key === "Enter" || e.key === ",") && keywordInput.trim()) {
      e.preventDefault();
      const kw = keywordInput.trim().replace(/,$/, "");
      if (!keywords.includes(kw) && keywords.length < keywordLimit) {
        setKeywords((prev) => [...prev, kw]);
      }
      setKeywordInput("");
    }
  }, [keywordInput, keywords, keywordLimit]);

  const handleKeywordRemove = useCallback((kw) => {
    setKeywords((prev) => prev.filter((k) => k !== kw));
  }, []);

  // ── copy to clipboard ──
  const handleCopy = useCallback((text, key) => {
    navigator.clipboard.writeText(typeof text === "object" ? JSON.stringify(text, null, 2) : String(text))
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
      });
  }, []);

  // ── main generate ──
  const handleGenerate = useCallback(async () => {
    if (!content.trim() && !sourceUrl.trim()) return;
    if (isOverLimit) return;
    if (!user) { setAuthModalOpen(true); return; }

    setIsGenerating(true);
    setOutput(null);
    setGenerateError(null);
    setLoadingProgress(0);
    setLoadingStep(LOADING_STEPS[0]);

    let stepIdx = 0;
    const stepTimer = setInterval(() => {
      stepIdx = Math.min(stepIdx + 1, LOADING_STEPS.length - 1);
      setLoadingStep(LOADING_STEPS[stepIdx]);
      setLoadingProgress(Math.round(((stepIdx + 1) / LOADING_STEPS.length) * 85));
    }, 700);

    try {
      const result = await generateSeoContent({
        platforms: selectedPlatforms,
        sourceUrl,
        description: content,
        keywords,
        tone,
        audience,
        contentLength: length,
        language,
      });

      clearInterval(stepTimer);
      setLoadingStep("Complete!");
      setLoadingProgress(100);
      setOutput(result);
      setIsGenerating(false);

      const historyEntry = {
        id: Date.now(),
        platforms: selectedPlatforms,
        title: result.seoTitle,
        score: result.seoScore.overall,
        createdAt: result.generatedAt,
        content: content.slice(0, 100),
      };
      setHistory((prev) => {
        const next = [historyEntry, ...prev].slice(0, 50);
        localStorage.setItem("tm_seo_history", JSON.stringify(next));
        return next;
      });

      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      clearInterval(stepTimer);
      setIsGenerating(false);
      setLoadingProgress(0);
      setGenerateError(formatApiError(err));
    }
  }, [content, sourceUrl, keywords, tone, audience, length, language, selectedPlatforms, isOverLimit, user, setAuthModalOpen]);

  const radarData = output
    ? [
        { subject: "Readability", value: output.seoScore.readability },
        { subject: "Keywords", value: output.seoScore.keywords },
        { subject: "Trending", value: output.seoScore.trending },
        { subject: "Engagement", value: output.seoScore.engagement },
        { subject: "Overall", value: output.seoScore.overall },
      ]
    : [];

  const outputTabs = [
    { id: "seo", label: "SEO" },
    { id: "social", label: "Social" },
    { id: "schema", label: "Schema" },
    { id: "hooks", label: "Hooks" },
  ];

  const filteredHistory = history.filter(
    (h) =>
      !historySearch ||
      h.title?.toLowerCase().includes(historySearch.toLowerCase()) ||
      h.platforms?.some((p) => p.includes(historySearch.toLowerCase())),
  );

  const inputClass = "w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-colors";
  const selectClass = "w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 pr-8 text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-colors";
  const labelClass = "text-[10px] uppercase tracking-[0.15em] text-slate-400";

  if (!user) {
    return (
      <>
        <SEO
          title="SEO Studio — Generate Platform-Optimized Content"
          description="Generate SEO titles, meta descriptions, hashtags, schema markup, social captions, viral hooks, and more. Optimized for Google, Instagram, LinkedIn, TikTok, and 10+ platforms."
          canonical="https://trendming.com/seo-studio"
        />
        <div className="min-h-screen flex items-center justify-center px-4 bg-white">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 border border-blue-200 mb-6">
              <MagicWand size={28} weight="fill" className="text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Sign in to use SEO Studio</h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Generate platform-optimized SEO content, meta tags, captions, viral hooks, and schema markup — in seconds.
            </p>
            <button
              onClick={() => setAuthModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-colors"
            >
              Sign In to Continue
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="SEO Studio — Generate Platform-Optimized Content"
        description="Generate SEO titles, meta descriptions, hashtags, schema markup, social captions, viral hooks, and more. Optimized for Google, Instagram, LinkedIn, TikTok, and 10+ platforms."
        canonical="https://trendming.com/seo-studio"
      />

      <div className="min-h-screen bg-white" data-testid="seo-studio-page">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden border-b border-slate-200">
          {/* Ambient gradient */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/40 rounded-full blur-[120px]" />
            <div className="absolute top-10 right-1/4 w-80 h-80 bg-violet-100/30 rounded-full blur-[100px]" />
          </div>

          <div className="relative max-w-[1600px] mx-auto px-4 md:px-8 py-8 md:py-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-slate-200 bg-slate-50 text-xs text-slate-500 uppercase tracking-wider rounded-lg">
                  <MagicWand size={11} className="text-blue-600" />
                  Smart Optimization
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-slate-200 bg-slate-50 text-xs text-slate-500 uppercase tracking-wider rounded-lg">
                  <Globe size={11} />
                  10 Platforms
                </span>
                {!isPremium && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-200 bg-amber-50 text-xs text-amber-600 uppercase tracking-wider rounded-lg">
                    Free tier
                  </span>
                )}
              </div>

              <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-tight mb-5">
                SEO Studio
              </h1>
              <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-xl mb-8">
                Generate platform-optimized SEO content, meta tags, captions, viral hooks, and schema markup — in seconds.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                {!isPremium && (
                  <Link
                    to="/pricing"
                    className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-slate-700 font-semibold text-sm rounded-lg transition-all"
                  >
                    <Crown size={14} className="text-violet-500" />
                    Upgrade Premium
                  </Link>
                )}
              </div>

              {!isPremium && (
                <p className="mt-4 text-[11px] text-slate-400">
                  Free tier: {FREE_DAILY_LIMIT} generations/day · {FREE_CONTENT_LIMIT} char limit · {FREE_PLATFORM_LIMIT} platform
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── Platform Selector ─────────────────────────────────────────────── */}
        <section className="border-b border-slate-200 py-6" data-testid="platform-selector">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
              <div>
                <h2 className="font-bold text-lg text-slate-900">
                  Target Platform
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  {isPremium ? "Select multiple platforms for optimized output" : "Free tier: 1 platform — upgrade for multi-platform optimization"}
                </p>
              </div>
              {!isPremium && selectedPlatforms.length >= FREE_PLATFORM_LIMIT && (
                <Link to="/pricing" className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-700 transition-colors">
                  <Crown size={11} />
                  Unlock all platforms
                  <ArrowRight size={10} />
                </Link>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-2">
              {PLATFORMS.map((platform) => (
                <PlatformCard
                  key={platform.id}
                  platform={platform}
                  selected={selectedPlatforms.includes(platform.id)}
                  onToggle={handlePlatformToggle}
                  disabled={!isPremium && !selectedPlatforms.includes(platform.id) && selectedPlatforms.length >= FREE_PLATFORM_LIMIT}
                />
              ))}
            </div>

            {!isPremium && (
              <div className="mt-4">
                <UpgradeBanner
                  message="Unlock multi-platform optimization — generate tailored content for all 10 platforms simultaneously with Premium."
                  compact
                />
              </div>
            )}
          </div>
        </section>

        {/* ── Input + Output ────────────────────────────────────────────────── */}
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* ── Left: Input Panel ── */}
            <div className="space-y-5 lg:sticky lg:top-24" data-testid="input-panel">
              <div>
                <h2 className="font-bold text-xl text-slate-900 mb-1">
                  Content Input
                </h2>
                <p className="text-xs text-slate-400">Paste a URL, write content, or add a description. The more detail, the better the output.</p>
              </div>

              {/* Source URL */}
              <div className="space-y-1.5">
                <label className={labelClass}>Source URL</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <LinkIcon size={13} />
                  </div>
                  <input
                    type="url"
                    value={sourceUrl}
                    onChange={(e) => setSourceUrl(e.target.value)}
                    placeholder="https://example.com/article-or-product"
                    className={`${inputClass} pl-9`}
                    data-testid="source-url-input"
                  />
                </div>
                {sourceUrl && !sourceUrl.startsWith("http://") && !sourceUrl.startsWith("https://") && (
                  <p className="text-[10px] text-amber-600">Include the full URL with https:// (e.g. https://example.com)</p>
                )}
                {(!sourceUrl || sourceUrl.startsWith("http://") || sourceUrl.startsWith("https://")) && (
                  <p className="text-[10px] text-slate-400">Paste an article, product page, or video URL to auto-extract content</p>
                )}
              </div>

              {/* Content Textarea */}
              <div className="space-y-1.5">
                <label className={labelClass}>Content / Description</label>
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => {
                      if (e.target.value.length <= contentLimit) setContent(e.target.value);
                    }}
                    placeholder={`Describe your content, article topic, or paste the full text here…\n\nExample: "A comprehensive guide to mastering TypeScript generics for React developers — covering advanced patterns, real-world use cases, and performance tips."`}
                    rows={5}
                    className={cn(
                      "w-full bg-slate-50 border rounded-lg py-3 px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors resize-none min-h-[120px]",
                      isOverLimit ? "border-red-300 focus:border-red-400" : isNearLimit ? "border-amber-300 focus:border-amber-400" : "border-slate-200 focus:border-blue-500",
                    )}
                    data-testid="content-textarea"
                  />
                </div>

                <CharCounter current={charCount} max={contentLimit} label="Optimized for AI token efficiency" isPremium={isPremium} />

                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-slate-400">
                    ~{tokenEstimate} tokens estimated
                  </p>
                  {!isPremium && isNearLimit && (
                    <Link to="/pricing" className="text-[10px] text-violet-600 hover:text-violet-700 flex items-center gap-1 transition-colors">
                      <Crown size={9} />Unlock 5,000 char limit
                    </Link>
                  )}
                </div>

                {isOverLimit && (
                  <p className="text-xs text-red-500 flex items-center gap-1.5">
                    <Warning size={12} />
                    Content exceeds the {contentLimit.toLocaleString()} character limit for your tier
                  </p>
                )}

                {!isPremium && charCount >= FREE_CONTENT_LIMIT * 0.9 && !isOverLimit && (
                  <UpgradeBanner
                    message="Unlock long-form SEO generation with Premium — up to 5,000 characters for in-depth content analysis."
                    compact
                  />
                )}
              </div>

              {/* Keywords */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className={labelClass}>Target Keywords</label>
                  <span className="text-[10px] text-slate-400">
                    {keywords.length} / {keywordLimit}
                  </span>
                </div>

                {keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    {keywords.map((kw) => (
                      <KeywordTag key={kw} keyword={kw} onRemove={handleKeywordRemove} />
                    ))}
                  </div>
                )}

                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <Tag size={12} />
                  </div>
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordAdd}
                    disabled={keywords.length >= keywordLimit}
                    placeholder={keywords.length >= keywordLimit ? `Max ${keywordLimit} keywords reached` : "Type keyword, press Enter or comma to add…"}
                    className={`${inputClass} pl-9`}
                    data-testid="keyword-input"
                  />
                </div>

                {!isPremium && keywords.length >= FREE_KEYWORD_LIMIT && (
                  <UpgradeBanner message="Add up to 25 keywords with Premium for deeper semantic coverage and better ranking potential." compact />
                )}
              </div>

              {/* Settings Row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Tone */}
                <div className="space-y-1.5">
                  <label className={labelClass}>Tone</label>
                  <div className="relative">
                    <select
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      className={selectClass}
                      data-testid="tone-select"
                    >
                      {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <CaretDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Audience */}
                <div className="space-y-1.5">
                  <label className={labelClass}>Audience</label>
                  <div className="relative">
                    <select
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      className={selectClass}
                      data-testid="audience-select"
                    >
                      {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <CaretDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Content Length */}
                <div className="space-y-1.5">
                  <label className={labelClass}>Length</label>
                  <div className="relative">
                    <select
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      className={selectClass}
                    >
                      {LENGTHS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <CaretDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Language */}
                <div className="space-y-1.5">
                  <label className={labelClass}>Language</label>
                  <div className="relative">
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className={selectClass}
                    >
                      {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <CaretDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || (!content.trim() && !sourceUrl.trim()) || isOverLimit}
                className={cn(
                  "w-full flex items-center justify-center gap-2.5 py-3 font-semibold text-sm rounded-lg transition-all",
                  "bg-blue-600 hover:bg-blue-700 text-white",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "active:scale-[0.99]",
                )}
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <ArrowClockwise size={15} className="animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <MagicWand size={15} weight="fill" />
                    Generate SEO Content
                  </>
                )}
              </button>

              {!content.trim() && !sourceUrl.trim() && (
                <p className="text-[11px] text-slate-400 text-center">Add content or a URL above to get started</p>
              )}
            </div>

            {/* ── Right: Output Panel ── */}
            <div ref={outputRef} data-testid="output-panel">
              {/* Loading State */}
              {isGenerating && (
                <div className="border border-slate-200 bg-white rounded-xl p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full border-2 border-blue-200 flex items-center justify-center">
                        <Robot size={16} className="text-blue-600" />
                      </div>
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-600 rounded-full animate-pulse" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">SEO Studio</div>
                      <div className="text-xs text-slate-400">{loadingStep}</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[10px] text-slate-400">
                      <span>Generating output…</span>
                      <span>{loadingProgress}%</span>
                    </div>
                    <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-600 to-violet-500 rounded-full transition-all duration-500"
                        style={{ width: `${loadingProgress}%` }}
                      />
                    </div>
                  </div>

                  {/* Skeleton placeholders */}
                  <div className="space-y-3">
                    {[80, 60, 90, 50].map((w, i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="h-2 bg-slate-200 rounded-full shimmer" style={{ width: "30%" }} />
                        <div className="h-3 bg-slate-200 rounded-full shimmer" style={{ width: `${w}%` }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error state */}
              {!isGenerating && generateError && !output && (
                <>
                  {/quota|limit|upgrade|premium/i.test(generateError) ? (
                    <div className="border border-violet-200 bg-violet-50 rounded-xl p-5 space-y-3">
                      <div className="flex items-start gap-3">
                        <Crown size={15} className="text-violet-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-violet-700 mb-0.5">Daily limit reached</p>
                          <p className="text-xs text-violet-600 leading-relaxed">{generateError}</p>
                        </div>
                        <button
                          onClick={() => setGenerateError(null)}
                          className="text-slate-400 hover:text-slate-600 transition-colors shrink-0"
                          aria-label="Dismiss error"
                        >
                          <X size={13} />
                        </button>
                      </div>
                      <Link
                        to="/pricing"
                        className="flex items-center justify-center gap-2 w-full py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg transition-colors"
                      >
                        <ArrowUpRight size={12} />
                        Upgrade to Premium — Unlimited Generations
                      </Link>
                    </div>
                  ) : (
                    <div className="border border-red-200 bg-red-50 rounded-xl p-4 flex items-start gap-3">
                      <Warning size={15} className="text-red-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-700 mb-0.5">Generation failed</p>
                        <p className="text-xs text-red-500 leading-relaxed">{generateError}</p>
                      </div>
                      <button
                        onClick={() => setGenerateError(null)}
                        className="text-slate-400 hover:text-slate-600 transition-colors shrink-0"
                        aria-label="Dismiss error"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Empty state */}
              {!isGenerating && !output && !generateError && (
                <div className="border border-dashed border-slate-300 rounded-xl p-10 flex flex-col items-center text-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                    <Sparkle size={22} className="text-blue-600/60" />
                  </div>
                  <div>
                    <p className="text-slate-600 font-medium mb-1">No output yet</p>
                    <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
                      Add your content on the left and click Generate SEO Content to get instant optimization.
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {["SEO Title", "Meta Tags", "Hashtags", "Schema", "Viral Hooks"].map((label) => (
                      <span key={label} className="px-2 py-1 bg-slate-100 border border-slate-200 text-[10px] text-slate-400 rounded">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated Output */}
              {!isGenerating && output && (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      <span className="text-sm font-semibold text-slate-900">Generated Output</span>
                      <span className="text-[10px] text-slate-400">
                        {new Date(output.generatedAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleGenerate}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg transition-all"
                        data-testid="regenerate-btn"
                      >
                        <ArrowClockwise size={11} />
                        Regenerate
                      </button>
                    </div>
                  </div>

                  {/* SERP Preview */}
                  <div className="border border-slate-200 bg-white rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-2">
                      <Eye size={11} />SERP Preview
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-slate-400 truncate">{output.ogTags?.url}</div>
                      <div className="text-blue-600 text-sm font-medium leading-snug hover:underline cursor-pointer">
                        {output.seoTitle}
                      </div>
                      <div className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                        {output.metaDescription}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-[10px]">
                      <span className={output.seoTitle?.length > 60 ? "text-amber-500" : "text-slate-400"}>
                        Title: {output.seoTitle?.length ?? 0}/60 chars
                        {output.seoTitle?.length > 60 && " ⚠ too long"}
                      </span>
                      <span className={
                        output.metaDescription?.length < 140 || output.metaDescription?.length > 160
                          ? "text-amber-500"
                          : "text-slate-400"
                      }>
                        Desc: {output.metaDescription?.length ?? 0}/160 chars
                        {output.metaDescription?.length < 140 && " ⚠ too short"}
                        {output.metaDescription?.length > 160 && " ⚠ too long"}
                      </span>
                    </div>
                  </div>

                  {/* Output Tabs */}
                  <div className="flex border-b border-slate-200 gap-0">
                    {outputTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveOutputTab(tab.id)}
                        className={cn(
                          "px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors",
                          activeOutputTab === tab.id
                            ? "border-blue-600 text-blue-600"
                            : "border-transparent text-slate-400 hover:text-slate-700",
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* SEO Tab */}
                  {activeOutputTab === "seo" && (
                    <div className="space-y-3">
                      <OutputCard
                        title="SEO Title"
                        content={output.seoTitle}
                        icon={FileText}
                        onCopy={() => handleCopy(output.seoTitle, "seoTitle")}
                        copied={copiedKey === "seoTitle"}
                      />
                      <OutputCard
                        title="Meta Description"
                        content={output.metaDescription}
                        icon={Tag}
                        onCopy={() => handleCopy(output.metaDescription, "metaDesc")}
                        copied={copiedKey === "metaDesc"}
                      />
                      <OutputCard
                        title="Keywords"
                        content={output.keywords}
                        icon={Hash}
                        onCopy={() => handleCopy(output.keywords.join(", "), "keywords")}
                        copied={copiedKey === "keywords"}
                      />
                      <OutputCard
                        title="Hashtags"
                        content={output.hashtags.join("  ")}
                        icon={Hash}
                        onCopy={() => handleCopy(output.hashtags.join(" "), "hashtags")}
                        copied={copiedKey === "hashtags"}
                      />
                      <OutputCard
                        title="URL Slug"
                        content={output.slug}
                        icon={LinkIcon}
                        mono
                        onCopy={() => handleCopy(output.slug, "slug")}
                        copied={copiedKey === "slug"}
                      />
                      <OutputCard
                        title="SEO Snippet HTML"
                        content={output.seoSnippet}
                        icon={Code}
                        mono
                        onCopy={() => handleCopy(output.seoSnippet, "seoSnippet")}
                        copied={copiedKey === "seoSnippet"}
                        premium={!isPremium}
                        locked={!isPremium}
                      />
                    </div>
                  )}

                  {/* Social Tab */}
                  {activeOutputTab === "social" && (
                    <div className="space-y-3">
                      <OutputCard
                        title="OG Tags"
                        content={`og:title: ${output.ogTags.title}\nog:description: ${output.ogTags.description}\nog:url: ${output.ogTags.url}\nog:type: ${output.ogTags.type}`}
                        icon={Globe}
                        mono
                        onCopy={() => handleCopy(JSON.stringify(output.ogTags, null, 2), "ogTags")}
                        copied={copiedKey === "ogTags"}
                        premium={!isPremium}
                        locked={!isPremium}
                      />
                      <OutputCard
                        title="Twitter Card"
                        content={`twitter:card: ${output.twitterCard.card}\ntwitter:title: ${output.twitterCard.title}\ntwitter:description: ${output.twitterCard.description}\ntwitter:site: ${output.twitterCard.site}`}
                        icon={XLogo}
                        mono
                        onCopy={() => handleCopy(JSON.stringify(output.twitterCard, null, 2), "twitterCard")}
                        copied={copiedKey === "twitterCard"}
                        premium={!isPremium}
                        locked={!isPremium}
                      />
                      {selectedPlatforms.includes("instagram") && (
                        <OutputCard
                          title="Instagram Caption"
                          content={output.socialCaptions.instagram}
                          icon={InstagramLogo}
                          onCopy={() => handleCopy(output.socialCaptions.instagram, "igCaption")}
                          copied={copiedKey === "igCaption"}
                        />
                      )}
                      {selectedPlatforms.includes("linkedin") && (
                        <OutputCard
                          title="LinkedIn Post"
                          content={output.socialCaptions.linkedin}
                          icon={LinkedinLogo}
                          onCopy={() => handleCopy(output.socialCaptions.linkedin, "liPost")}
                          copied={copiedKey === "liPost"}
                        />
                      )}
                      {selectedPlatforms.includes("reddit") && (
                        <OutputCard
                          title="Reddit Thread Title"
                          content={output.socialCaptions.reddit}
                          icon={RedditLogo}
                          onCopy={() => handleCopy(output.socialCaptions.reddit, "redditTitle")}
                          copied={copiedKey === "redditTitle"}
                        />
                      )}
                      {selectedPlatforms.includes("facebook") && (
                        <OutputCard
                          title="Facebook Post"
                          content={output.socialCaptions.facebook}
                          icon={FacebookLogo}
                          onCopy={() => handleCopy(output.socialCaptions.facebook, "fbPost")}
                          copied={copiedKey === "fbPost"}
                        />
                      )}
                      {selectedPlatforms.includes("tiktok") && (
                        <OutputCard
                          title="TikTok Caption"
                          content={output.socialCaptions.tiktok}
                          icon={TiktokLogo}
                          onCopy={() => handleCopy(output.socialCaptions.tiktok, "ttCaption")}
                          copied={copiedKey === "ttCaption"}
                        />
                      )}
                      {selectedPlatforms.includes("youtube") && (
                        <OutputCard
                          title="YouTube Description"
                          content={output.socialCaptions.youtube}
                          icon={YoutubeLogo}
                          onCopy={() => handleCopy(output.socialCaptions.youtube, "ytTitle")}
                          copied={copiedKey === "ytTitle"}
                        />
                      )}
                      {selectedPlatforms.includes("x") && (
                        <OutputCard
                          title="X (Twitter) Tweet"
                          content={output.socialCaptions.x}
                          icon={XLogo}
                          onCopy={() => handleCopy(output.socialCaptions.x, "xTweet")}
                          copied={copiedKey === "xTweet"}
                        />
                      )}
                      {selectedPlatforms.includes("pinterest") && (
                        <OutputCard
                          title="Pinterest Description"
                          content={output.socialCaptions.pinterest}
                          icon={PinterestLogo}
                          onCopy={() => handleCopy(output.socialCaptions.pinterest, "pinDesc")}
                          copied={copiedKey === "pinDesc"}
                        />
                      )}
                      {selectedPlatforms.includes("medium") && (
                        <OutputCard
                          title="Medium Subtitle"
                          content={output.socialCaptions.medium}
                          icon={BookOpen}
                          onCopy={() => handleCopy(output.socialCaptions.medium, "medSub")}
                          copied={copiedKey === "medSub"}
                        />
                      )}
                      {selectedPlatforms.includes("google") && (
                        <OutputCard
                          title="Google Rich Snippet"
                          content={output.socialCaptions.google}
                          icon={MagnifyingGlass}
                          onCopy={() => handleCopy(output.socialCaptions.google, "googleSnippet")}
                          copied={copiedKey === "googleSnippet"}
                        />
                      )}
                      <OutputCard
                        title="ALT Text"
                        content={output.altText}
                        icon={Eye}
                        onCopy={() => handleCopy(output.altText, "altText")}
                        copied={copiedKey === "altText"}
                      />
                      <OutputCard
                        title="CTA Suggestions"
                        content={output.cta}
                        icon={Target}
                        onCopy={() => handleCopy(output.cta.join("\n"), "cta")}
                        copied={copiedKey === "cta"}
                      />
                    </div>
                  )}

                  {/* Schema Tab */}
                  {activeOutputTab === "schema" && (
                    <div className="space-y-3">
                      {isPremium ? (
                        <>
                          <OutputCard
                            title="JSON-LD Schema"
                            content={output.jsonLd}
                            icon={Code}
                            mono
                            onCopy={() => handleCopy(output.jsonLd, "jsonLd")}
                            copied={copiedKey === "jsonLd"}
                          />
                          <OutputCard
                            title="FAQ Schema"
                            content={output.faqSchema}
                            icon={BookOpen}
                            mono
                            onCopy={() => handleCopy(output.faqSchema, "faqSchema")}
                            copied={copiedKey === "faqSchema"}
                          />
                        </>
                      ) : (
                        <div className="space-y-4">
                          <div className="border border-dashed border-slate-300 rounded-xl p-8 text-center space-y-4">
                            <div className="w-12 h-12 mx-auto bg-violet-50 border border-violet-200 rounded-xl flex items-center justify-center">
                              <Code size={20} className="text-violet-600" />
                            </div>
                            <div>
                              <p className="text-slate-900 font-semibold mb-1">Schema Markup Generation</p>
                              <p className="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">
                                JSON-LD and FAQ schema generation is a Premium feature. Structured data can dramatically improve your SERP appearance.
                              </p>
                            </div>
                            <UpgradeBanner message="Unlock JSON-LD, FAQ Schema, and advanced metadata generation with Premium." />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hooks Tab */}
                  {activeOutputTab === "hooks" && (
                    <div className="space-y-3">
                      <OutputCard
                        title="Viral Hooks"
                        content={output.viralHooks}
                        icon={Fire}
                        onCopy={() => handleCopy(output.viralHooks.join("\n\n"), "hooks")}
                        copied={copiedKey === "hooks"}
                      />
                      <OutputCard
                        title="SEO Suggestions"
                        content={output.aiSuggestions}
                        icon={Lightbulb}
                        onCopy={() => handleCopy(output.aiSuggestions.join("\n"), "suggestions")}
                        copied={copiedKey === "suggestions"}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── SEO Score Panel ───────────────────────────────────────────────── */}
        {output && (
          <section className="border-t border-slate-200 bg-slate-50 py-8" data-testid="seo-score-panel">
            <div className="max-w-[1600px] mx-auto px-4 md:px-8">
              <div className="flex items-end justify-between mb-5">
                <div>
                  <h2 className="font-bold text-2xl text-slate-900">SEO Score</h2>
                  <p className="text-xs text-slate-400 mt-1">AI-calculated content quality and optimization metrics</p>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="text-4xl font-bold"
                    style={{ color: output.seoScore.overall >= 80 ? "#22c55e" : output.seoScore.overall >= 60 ? "#f59e0b" : "#ef4444" }}
                  >
                    {output.seoScore.overall}
                  </div>
                  <div className="text-slate-400 text-sm">/100</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Score rings */}
                <div className="border border-slate-200 bg-white rounded-xl p-6">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-6">Score breakdown</div>
                  <div className="grid grid-cols-5 gap-2">
                    <ScoreRing score={output.seoScore.overall} label="Overall" color="#2563EB" />
                    <ScoreRing score={output.seoScore.readability} label="Readability" color="#3b82f6" />
                    <ScoreRing score={output.seoScore.keywords} label="Keywords" color="#10b981" />
                    <ScoreRing score={output.seoScore.trending} label="Trending" color="#f59e0b" />
                    <ScoreRing score={output.seoScore.engagement} label="Engagement" color="#8b5cf6" />
                  </div>
                </div>

                {/* Radar chart */}
                <div className="border border-slate-200 bg-white rounded-xl p-6">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-4">Performance radar</div>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                        <PolarGrid stroke="rgba(0,0,0,0.08)" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: "#64748B", fontSize: 10 }} />
                        <Radar
                          name="Score"
                          dataKey="value"
                          stroke="#2563EB"
                          fill="#2563EB"
                          fillOpacity={0.12}
                          strokeWidth={1.5}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── History ───────────────────────────────────────────────────────── */}
        <section className="border-t border-slate-100 py-8" data-testid="history-section">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-xl text-slate-900">Generation History</h2>
                <p className="text-xs text-slate-400 mt-0.5">{history.length} saved generations</p>
              </div>
              <button
                onClick={() => setShowHistory((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-500 hover:text-slate-900 text-xs rounded-lg transition-colors"
              >
                <Clock size={12} />
                {showHistory ? "Hide" : "Show"} History
                <CaretDown size={10} className={cn("transition-transform", showHistory && "rotate-180")} />
              </button>
            </div>

            {showHistory && (
              <div className="space-y-4">
                {!user ? (
                  <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
                    <Lock size={20} className="text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium mb-1">Sign in to save history</p>
                    <p className="text-sm text-slate-400 mb-4">Your generations are currently saved locally. Sign in to sync across devices.</p>
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Sign in
                    </button>
                  </div>
                ) : history.length === 0 ? (
                  <div className="border border-dashed border-slate-200 rounded-xl p-8 text-center">
                    <Clock size={20} className="text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-400">No history yet — generate your first SEO content above.</p>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    <div className="relative max-w-sm">
                      <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        placeholder="Search history…"
                        className={`${inputClass} pl-9`}
                      />
                    </div>

                    {!isPremium && (
                      <UpgradeBanner message="Premium unlocks full history, favorites, and CSV export." compact />
                    )}

                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                        {["Title", "Platforms", "Score", "Date"].map((h) => (
                          <div key={h} className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{h}</div>
                        ))}
                      </div>
                      {filteredHistory.slice(0, isPremium ? undefined : 5).map((item) => (
                        <div
                          key={item.id}
                          className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                          data-testid={`history-item-${item.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm text-slate-700 truncate">{item.title}</div>
                            {item.content && (
                              <div className="text-[10px] text-slate-400 truncate mt-0.5">{item.content}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-wrap justify-end">
                            {(item.platforms || []).slice(0, 3).map((p) => {
                              const pl = PLATFORMS.find((pl) => pl.id === p);
                              return pl ? (
                                <span key={p} className="text-[11px] uppercase px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                                  {pl.label.split(" ")[0]}
                                </span>
                              ) : null;
                            })}
                          </div>
                          <div
                            className="text-sm font-bold tabular-nums"
                            style={{ color: item.score >= 80 ? "#22c55e" : item.score >= 60 ? "#f59e0b" : "#ef4444" }}
                          >
                            {item.score}
                          </div>
                          <div className="text-[10px] text-slate-400 whitespace-nowrap">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>

                    {!isPremium && history.length > 5 && (
                      <UpgradeBanner message={`${history.length - 5} older entries hidden — unlock full history with Premium.`} compact />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </section>

      </div>
    </>
  );
}
